import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';

// --- Types ---

interface PublicContractData {
  id: number;
  property_title: string;
  contract_type: string;
  monthly_amount: number;
  currency: string;
  start_date: string;
  end_date: string;
  terms: string;
  company_name: string | null;
  status: string;
}

interface PublicContractResponse {
  data: PublicContractData;
}

interface SignErrorResponse {
  error?: { code?: string; message?: string };
  message?: string;
}

type PageState = 'loading' | 'ready' | 'signed' | 'error';

// --- Constants ---

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const CONTRACT_TYPE_LABEL: Record<string, string> = {
  rent: 'Alquiler',
  sale: 'Venta',
  reservation: 'Reserva',
};

const ERROR_MESSAGES: Record<string, string> = {
  token_expired: 'El enlace de firma ha expirado. Solicita uno nuevo a tu inmobiliaria.',
  already_signed: 'Este contrato ya fue firmado.',
  invalid_token: 'El enlace de firma no es valido. Verifica que el enlace sea correcto.',
  not_found: 'Contrato no encontrado.',
};

// --- Helpers ---

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency || 'ARS',
    maximumFractionDigits: 0,
  }).format(amount);
}

// --- Component ---

export default function PublicSignPage(): React.ReactElement {
  const { token } = useParams<{ token: string }>();

  const sigCanvasRef = useRef<SignatureCanvas>(null);

  const [pageState, setPageState] = useState<PageState>('loading');
  const [contract, setContract] = useState<PublicContractData | null>(null);
  const [signerName, setSignerName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Fetch contract data on mount (no auth)
  useEffect(() => {
    if (!token) {
      setErrorMessage('Enlace de firma invalido.');
      setPageState('error');
      return;
    }

    axios
      .get<PublicContractResponse>(`${API_URL}/v1/contracts/sign/${token}`)
      .then((res) => {
        const data = res.data?.data;
        if (!data) {
          setErrorMessage(ERROR_MESSAGES.not_found);
          setPageState('error');
          return;
        }
        if (data.status === 'active' || data.status === 'signed') {
          setErrorMessage(ERROR_MESSAGES.already_signed);
          setPageState('error');
          return;
        }
        setContract(data);
        setPageState('ready');
      })
      .catch((err) => {
        const status = err.response?.status;
        const errData = err.response?.data as SignErrorResponse | undefined;
        const code = errData?.error?.code;

        if (status === 410 || code === 'token_expired') {
          setErrorMessage(ERROR_MESSAGES.token_expired);
        } else if (status === 409 || code === 'already_signed') {
          setErrorMessage(ERROR_MESSAGES.already_signed);
        } else if (status === 404 || code === 'invalid_token' || code === 'not_found') {
          setErrorMessage(ERROR_MESSAGES.invalid_token);
        } else {
          setErrorMessage(
            errData?.error?.message || errData?.message || 'Error al cargar el contrato. Intenta nuevamente.'
          );
        }
        setPageState('error');
      });
  }, [token]);

  const handleClear = (): void => {
    sigCanvasRef.current?.clear();
    setValidationError('');
  };

  const handleSign = async (): Promise<void> => {
    // Validate
    if (!signerName.trim()) {
      setValidationError('Ingresa tu nombre completo.');
      return;
    }
    if (!termsAccepted) {
      setValidationError('Debes aceptar los terminos del contrato.');
      return;
    }
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      setValidationError('Dibuja tu firma antes de confirmar.');
      return;
    }

    setValidationError('');
    setIsSubmitting(true);

    try {
      const signatureData = sigCanvasRef.current.toDataURL('image/png');
      await axios.post(`${API_URL}/v1/contracts/sign/${token}`, {
        signature_data: signatureData,
        signer_name: signerName.trim(),
      });
      setPageState('signed');
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errData = err.response?.data as SignErrorResponse | undefined;
        const code = errData?.error?.code;
        if (code === 'already_signed' || err.response?.status === 409) {
          setErrorMessage(ERROR_MESSAGES.already_signed);
          setPageState('error');
        } else if (code === 'token_expired' || err.response?.status === 410) {
          setErrorMessage(ERROR_MESSAGES.token_expired);
          setPageState('error');
        } else {
          setValidationError(
            errData?.error?.message || errData?.message || 'Error al firmar. Intenta nuevamente.'
          );
        }
      } else {
        setValidationError('Error de conexion. Intenta nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Loading state ---
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  // --- Error state ---
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
        <div className="card bg-base-100 shadow-lg max-w-md w-full">
          <div className="card-body items-center text-center">
            {/* SGI Branding */}
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-primary">SGI</h2>
              <p className="text-xs text-base-content/50">Sistema de Gestion Inmobiliaria</p>
            </div>

            <div className="text-error mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>

            <h3 className="text-lg font-semibold">No se puede firmar</h3>
            <p className="text-base-content/70 mt-2">{errorMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  // --- Success state ---
  if (pageState === 'signed') {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
        <div className="card bg-base-100 shadow-lg max-w-md w-full">
          <div className="card-body items-center text-center">
            {/* SGI Branding */}
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-primary">SGI</h2>
              <p className="text-xs text-base-content/50">Sistema de Gestion Inmobiliaria</p>
            </div>

            <div className="text-success mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h3 className="text-xl font-semibold">Contrato firmado exitosamente</h3>
            <p className="text-base-content/70 mt-2">
              Tu firma ha sido registrada correctamente. La inmobiliaria sera notificada.
            </p>
            <p className="text-sm text-base-content/50 mt-4">
              Puedes cerrar esta ventana.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // --- Ready state (signing form) ---
  return (
    <div className="min-h-screen bg-base-200 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* SGI Branding Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary">SGI</h1>
          <p className="text-sm text-base-content/50">Sistema de Gestion Inmobiliaria</p>
        </div>

        {/* Contract details card */}
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-lg">Detalles del contrato</h2>

            {contract && (
              <div className="space-y-3 mt-2">
                <div className="flex justify-between">
                  <span className="text-base-content/60">Propiedad</span>
                  <span className="font-medium text-right">{contract.property_title}</span>
                </div>
                <div className="divider my-0" />

                {contract.company_name && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-base-content/60">Inmobiliaria</span>
                      <span className="font-medium">{contract.company_name}</span>
                    </div>
                    <div className="divider my-0" />
                  </>
                )}

                <div className="flex justify-between">
                  <span className="text-base-content/60">Tipo</span>
                  <span className="font-medium">
                    {CONTRACT_TYPE_LABEL[contract.contract_type] || contract.contract_type}
                  </span>
                </div>
                <div className="divider my-0" />

                <div className="flex justify-between">
                  <span className="text-base-content/60">Fecha de inicio</span>
                  <span className="font-medium">{formatDate(contract.start_date)}</span>
                </div>
                <div className="divider my-0" />

                <div className="flex justify-between">
                  <span className="text-base-content/60">Fecha de fin</span>
                  <span className="font-medium">{formatDate(contract.end_date)}</span>
                </div>
                <div className="divider my-0" />

                <div className="flex justify-between">
                  <span className="text-base-content/60">Monto mensual</span>
                  <span className="font-medium text-lg">
                    {formatPrice(contract.monthly_amount, contract.currency)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Terms card */}
        {contract?.terms && (
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h2 className="card-title text-lg">Terminos y condiciones</h2>
              <div
                className="prose prose-sm max-w-none mt-2 max-h-96 overflow-y-auto pr-2"
                dangerouslySetInnerHTML={{ __html: contract.terms }}
              />
            </div>
          </div>
        )}

        {/* Signing card */}
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-lg">Firma del contrato</h2>

            {/* Signer name */}
            <div className="form-control mt-4">
              <label className="label">
                <span className="label-text font-medium">Nombre completo del firmante</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="Ej: Juan Carlos Perez"
                value={signerName}
                onChange={(e) => {
                  setSignerName(e.target.value);
                  setValidationError('');
                }}
              />
            </div>

            {/* Signature canvas */}
            <div className="mt-4">
              <label className="label">
                <span className="label-text font-medium">Dibuja tu firma</span>
              </label>
              <div className="border-2 border-dashed border-base-300 rounded-lg bg-white">
                <SignatureCanvas
                  ref={sigCanvasRef}
                  canvasProps={{
                    className: 'w-full h-48',
                    style: { width: '100%', height: '192px' },
                  }}
                  backgroundColor="white"
                />
              </div>
              <button
                type="button"
                className="btn btn-outline btn-sm mt-2"
                onClick={handleClear}
              >
                Limpiar firma
              </button>
            </div>

            {/* Terms acceptance */}
            <div className="form-control mt-4">
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    setValidationError('');
                  }}
                />
                <span className="label-text">
                  He leido y acepto los terminos del contrato
                </span>
              </label>
            </div>

            {/* Validation error */}
            {validationError && (
              <div className="alert alert-error mt-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{validationError}</span>
              </div>
            )}

            {/* Sign button */}
            <button
              type="button"
              className="btn btn-success w-full mt-6"
              onClick={handleSign}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm" />
                  Firmando...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Firmar contrato
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-base-content/40 pb-8">
          Al firmar, se registrara tu nombre, firma, direccion IP y la fecha/hora actual.
        </p>
      </div>
    </div>
  );
}
